import React, { useEffect } from "react";
import { Box, useMediaQuery } from "@mui/material";
import { Outlet } from "react-router-dom";
import useSettingsNav from "../hooks/useSettingsNav";
import SettingsSidebar from "./SettingsSidebar";
import SettingsTabBar from "./SettingsTabBar";
import SettingsSectionSelector from "./SettingsSectionSelector";
import useTranslation from "../../../shared/hooks/useTranslation";
import { useAppDispatch } from "../../../shared/hooks/sharedHooks";
import { getAccount } from "../customerAccountSlice";
import { dt } from "../../../shared/themes/redesignTokens";

// ============================================================
//  DESIGN TOKENS
// ============================================================

const SettingsLayout: React.FC = () => {
  const isCompact = useMediaQuery("(max-width:1200px)");
  const isPhone = useMediaQuery("(max-width:768px)");
  const { sections } = useSettingsNav();
  const { translate } = useTranslation("settings");
  const dispatch = useAppDispatch();

  // Keyframe injection
  useEffect(() => {
    const styleId = "settings-redesign-keyframes";
    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      style.textContent = `
        @keyframes fadeUp {
          0% { opacity: 0; transform: translateY(14px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeLeft {
          0% { opacity: 0; transform: translateX(-12px); }
          100% { opacity: 1; transform: translateX(0); }
        }
        @keyframes headerReveal {
          0% { opacity: 0; transform: translateY(-8px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes cardReveal {
          0% { opacity: 0; transform: translateY(10px); }
          100% { opacity: 1; transform: translateY(0); }
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  // Load user account info for sidebar avatar
  useEffect(() => {
    dispatch(getAccount());
  }, [dispatch]);

  return (
    <Box
      sx={{
        backgroundColor: dt.pageBg,
        backgroundImage: `radial-gradient(circle at 1px 1px, ${dt.pageDotColor} 1px, transparent 0)`,
        backgroundSize: "28px 28px",
        fontFamily: dt.font,
        "& *:not(.MuiSvgIcon-root)": { fontFamily: `${dt.font}` },
        overflowX: "hidden",
        flex: 1,
        minHeight: 0,
      }}
    >
      {/* Page header */}
      <Box
        sx={{
          px: { xs: 2, md: 3 },
          pt: { xs: 2, md: 3 },
          pb: 1,
          animation: "headerReveal 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <Box
          sx={{
            fontSize: { xs: "20px", sm: "22px", md: "28px" },
            fontWeight: 800,
            color: dt.textPrimary,
            letterSpacing: "-0.5px",
            fontFamily: dt.font,
          }}
        >
          {translate("common:navigation.settings")}
        </Box>
        <Box
          sx={{
            width: "48px",
            height: "3px",
            borderRadius: "2px",
            background: `linear-gradient(90deg, ${dt.blue}, ${dt.blue}00)`,
            mt: 1,
          }}
        />
      </Box>

      {/* Desktop layout: sidebar + content */}
      {!isCompact && (
        <Box
          sx={{
            display: "flex",
            gap: "24px",
            px: 3,
            pt: 2,
            pb: 3,
            minWidth: 0,
          }}
        >
          <SettingsSidebar sections={sections} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Outlet />
          </Box>
        </Box>
      )}

      {/* Tablet layout: tabs + content */}
      {isCompact && !isPhone && (
        <Box sx={{ px: 2, pt: 1, pb: 3 }}>
          <SettingsTabBar sections={sections} />
          <Box sx={{ mt: 2 }}>
            <Outlet />
          </Box>
        </Box>
      )}

      {/* Phone layout: dropdown selector + content */}
      {isPhone && (
        <Box sx={{ px: "12px", pt: 1, pb: 3 }}>
          <SettingsSectionSelector sections={sections} />
          <Box sx={{ mt: 2 }}>
            <Outlet />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default SettingsLayout;
